import { Card, CardBody, CardFooter, HStack, Avatar, Box, Heading, Text, Badge, Button, Icon, Spinner } from '@chakra-ui/react';
import { FiAlertCircle } from 'react-icons/fi';
import { IoCarSportOutline } from 'react-icons/io5';
import { keyframes } from '@emotion/react';

// Re-defining pulse here or importing it? Better to import if shared, but for now copying to avoid dependency hell if I change it.
export const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.7); }
  70% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(229, 62, 62, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
`;

export const STATUS_CONFIG = {
    'NOT_ARRIVED': { label: '未到班', color: 'gray', priority: 6, isUrgent: false },
    'ARRIVED': { label: '已進班', color: 'blue', priority: 5, isUrgent: false },
    'HOMEWORK_PENDING': { label: '留班加強', color: 'orange', priority: 4, isUrgent: false },
    'READY_FOR_PICKUP': { label: '可接送', color: 'green', priority: 2, isUrgent: false },
    'PARENT_EN_ROUTE': { label: '家長路途中', color: 'red', priority: 1, isUrgent: true },
    'PICKUP_COMPLETED': { label: '已離校', color: 'gray', priority: 7, isUrgent: false },
};

const PickupStudentCard = ({ student, onUpdate, isUpdating, isCompleting }) => {
    const config = STATUS_CONFIG[student.status] || STATUS_CONFIG['NOT_ARRIVED'];
    const isUrgent = config.isUrgent && !isCompleting;

    return (
        <Card
            border="1px solid"
            borderColor="gray.100"
            borderLeftWidth="4px"
            borderLeftColor={`${config.color}.500`}
            shadow="sm"
            bg={(isCompleting || student.status === 'PICKUP_COMPLETED') ? 'gray.50' : 'white'}
            opacity={isCompleting ? 0.5 : (student.status === 'PICKUP_COMPLETED' ? 0.8 : 1)}
            height="auto"
            minH={{ base: "60px", md: "140px" }} // Mobile slim, Tablet+ vertical
            display="flex"
            flexDirection={{ base: "row", md: "column" }} // Responsive direction
            alignItems={{ base: "center", md: "stretch" }}
            size="sm"
            p={{ base: 2, md: 3 }}
            position="relative"
            overflow="hidden"
            transition="all 0.35s ease"
        >
            {/* 1. Name & Avatar Area */}
            <HStack flex={{ base: "1", md: "none" }} minW="0" mr={{ base: 2, md: 0 }} mb={{ md: 2 }} spacing={3}>
                <Avatar
                    size="sm"
                    name={student.full_name}
                    bg={`${config.color}.500`}
                    color="white"
                    display={{ base: "none", md: "inline-flex" }} // Only show on tablets+
                />
                <Box minW="0">
                    <Heading size={{ base: "xs", md: "sm" }} color="gray.700" isTruncated>
                        {student.full_name}
                    </Heading>
                    <Text fontSize={{ base: "10px", md: "xs" }} color="gray.400" isTruncated>
                        {student.class?.name || '未分班'}
                    </Text>
                </Box>
            </HStack>

            {/* 2. Status/ETA Area */}
            <Box
                flex={{ base: "1", md: "1" }}
                textAlign={{ base: "center", md: "left" }}
                mr={{ base: 2, md: 0 }}
                mb={{ md: 2 }}
                display="flex"
                alignItems="center"
                justifyContent={{ base: "center", md: "flex-start" }}
            >
                {isCompleting ? (
                    <Badge variant="solid" colorScheme="green" fontSize={{ base: "9px", md: "11px" }} px={2}>
                        ✓ 已離校
                    </Badge>
                ) : student.status === 'PARENT_EN_ROUTE' ? (
                    <Box
                        bg="red.50"
                        px={{ base: 2, md: 3 }}
                        py={{ base: 1, md: 2 }}
                        borderRadius="full"
                        border="1px solid"
                        borderColor="red.100"
                        animation={isUrgent ? `${pulse} 2s infinite` : 'none'}
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" color="red.600">
                            {(() => {
                                const hasDistance = student.distance_meters != null;
                                const hasEta = student.eta_minutes != null;
                                if (!hasDistance && !hasEta) return '定位中...';
                                if (student.distance_meters < 100) return '已抵達';
                                return `${student.eta_minutes}m`;
                            })()}
                        </Text>
                        <Text fontSize="10px" color="red.400" display={{ base: "none", md: "block" }}>路途中</Text>
                        {/* [A3] 本次接送車號：有值才顯示（天籟家長沒有登錄車號 → 恆不顯示，零影響）。
                            只在 md+ 顯示，避免手機版單行卡片被擠爆。 */}
                        {student.active_pickup_car_plate && (
                            <HStack
                                spacing={0.5}
                                display={{ base: "none", md: "flex" }}
                                borderLeft="1px solid"
                                borderColor="red.200"
                                pl={1}
                                ml={0.5}
                            >
                                <Icon as={IoCarSportOutline} boxSize={3} color="red.400" />
                                <Text fontSize="10px" color="red.500" fontWeight="bold" isTruncated maxW="70px">
                                    {student.active_pickup_car_plate}
                                </Text>
                            </HStack>
                        )}
                    </Box>
                ) : (
                    <Badge variant="subtle" colorScheme={config.color} fontSize={{ base: "9px", md: "11px" }} px={2}>
                        {config.label}
                    </Badge>
                )}
            </Box>

            {/* 3. Action Button */}
            <Box mt={{ base: 0, md: "auto" }}>
                {(!isCompleting && (student.status === 'PARENT_EN_ROUTE' || student.status === 'READY_FOR_PICKUP')) ? (
                    <Button
                        size={{ base: "xs", md: "sm" }}
                        colorScheme="green"
                        variant={student.status === 'PARENT_EN_ROUTE' ? "solid" : "outline"}
                        w={{ base: "auto", md: "100%" }}
                        px={{ base: 3, md: 4 }}
                        h={{ base: "28px", md: "32px" }}
                        isLoading={isUpdating}
                        onClick={(e) => {
                            e.stopPropagation();
                            onUpdate(student.id, 'PICKUP_COMPLETED');
                        }}
                    >
                        離校
                    </Button>
                ) : (
                    <Box w={{ base: "45px", md: "100%" }} h={{ md: "32px" }} />
                )}
            </Box>
        </Card>
    );
};

export default PickupStudentCard;
